import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { ModalProps } from 'featureSliced/shared/store/reducers/ModalsReducer/types'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { AddCommonProduct } from './modals/AddCommonProduct'
import { AddMenuItemsProduct } from './modals/AddMenuItemsProduct'
import { AddMenuItems } from './modals/AddMenuItems'
import { AddSubTemplateProduct } from './modals/AddSubTemplateProduct'
import { AddFixedTemplateProduct } from './modals/AddFixedTemplateProduct'
import { templateNeedsCompose } from 'featureSliced/shared/utils/menuStepChoices'
import { useGetTranslatedNames } from '../../useGetTranslatedNames'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'

export type OpenProductModalType = (
  product: IProduct,
  productTemplateId: ProductTemplateIdType
) => void

export const useOpenProduct = (): OpenProductModalType => {
  const openModal = useOpenModal()
  const { getProductName } = useGetTranslatedNames()
  const openProductModal = (
    product: IProduct,
    productTemplateId: ProductTemplateIdType
  ) => {
    const innerCategoryItems = product.innerCategoryItems

    const isCommonProduct =
      !innerCategoryItems?.length && !product.menus?.length
    const isSubtemplate = innerCategoryItems?.length
    const hasMenus = product.menus?.length && !innerCategoryItems?.length
    // a template whose every step is already decided skips the configurator
    // entirely and adds its fixed components with the article (QRM-665)
    const isMenuItems = hasMenus && templateNeedsCompose(product)
    const isFixedTemplate = hasMenus && !templateNeedsCompose(product)

    openModal({
      id: `ADD_PRODUCT_${product.id}`,
      title: isSubtemplate ? getProductName(product) : undefined,
      // web dialog only (the phone sheet ignores it): the default 560px cramps
      // the variant pickers of menu/set products
      dialogWidth: '640px',
      components: [
        ...(isCommonProduct
          ? [
              ({ closeThisModal }: ModalProps) => (
                <AddCommonProduct
                  product={product}
                  productTemplateId={productTemplateId}
                  closeProduct={closeThisModal}
                />
              ),
            ]
          : []),
        ...(isFixedTemplate
          ? [
              ({ closeThisModal }: ModalProps) => (
                <AddFixedTemplateProduct
                  product={product}
                  productTemplateId={productTemplateId}
                  closeProduct={closeThisModal}
                />
              ),
            ]
          : []),
        ...(isSubtemplate
          ? [
              ({ closeThisModal }: ModalProps) => (
                <AddSubTemplateProduct
                  products={product.innerCategoryItems}
                  productTemplateId={productTemplateId}
                  closeModal={closeThisModal}
                />
              ),
            ]
          : []),
        ...(isMenuItems
          ? [
              ({ onNext }: ModalProps) => (
                <AddMenuItemsProduct
                  product={product}
                  productTemplateId={productTemplateId}
                  onNext={onNext}
                />
              ),
              ({ closeThisModal, onNext, onPrev, ...props }: ModalProps) => (
                <AddMenuItems
                  product={product}
                  productTemplateId={productTemplateId}
                  closeModal={closeThisModal}
                  {...props}
                />
              ),
            ]
          : []),
      ],
    })
  }

  return openProductModal
}
